import React, { useState, useMemo, useEffect } from 'react';
import { useTable, useSortBy, usePagination } from 'react-table';
import './StudentsTable.css';
import SchoolUsers from './SchoolUsers';
import SchoolInvites from './SchoolInvites';

// Roster bulk-upload template + parser — mirrors the customer app (utils/studentCsv) so a sheet
// that works in the school portal also works here. Only Student ID / First Name / Last Name are
// required; Grade is optional. The header row is auto-detected among the first few lines, so both
// a plain CSV and the template (human-readable title on row 1, headers on row 2) import cleanly.
const STUDENT_TEMPLATE_CSV =
  "Student Import Template (Grade is optional - leave it blank if you don't track grades)\n" +
  'Student ID,First Name,Last Name,Grade (optional)\n' +
  '12345,John,Doe,9\n' +
  '12346,Jane,Smith,10\n' +
  '12347,Bob,Johnson,';

const ID_HEADERS = ['student id', 'student_id', 'studentid', 'id'];
const FIRST_HEADERS = ['first name', 'first_name', 'firstname', 'first'];
const LAST_HEADERS = ['last name', 'last_name', 'lastname', 'last'];
const findHeader = (headers, names) => headers.findIndex((h) => names.includes(h));

function parseRosterCsv(content) {
  const lines = content.replace(/^﻿/, '').trim().split(/\r?\n/).map((l) => l.trim()).filter((l) => l.length > 0);
  if (lines.length === 0) return { rows: [], error: 'That file looks empty. Download the template, add your students, and try again.' };
  let best = { index: -1, matches: -1, headers: [] };
  for (let i = 0; i < Math.min(lines.length, 5); i++) {
    const headers = lines[i].split(',').map((h) => h.trim().toLowerCase());
    const matches = [ID_HEADERS, FIRST_HEADERS, LAST_HEADERS].filter((names) => findHeader(headers, names) !== -1).length;
    if (matches > best.matches) best = { index: i, matches, headers };
    if (matches === 3) break;
  }
  if (best.matches < 3) {
    if (best.matches <= 0) return { rows: [], error: "We couldn't find the column headers. Your file needs a row with Student ID, First Name and Last Name (Grade is optional). Download the template for the exact format." };
    const missing = [];
    if (findHeader(best.headers, ID_HEADERS) === -1) missing.push('Student ID');
    if (findHeader(best.headers, FIRST_HEADERS) === -1) missing.push('First Name');
    if (findHeader(best.headers, LAST_HEADERS) === -1) missing.push('Last Name');
    return { rows: [], error: `Your file is missing a ${missing.join(' and ')} column. Required columns are Student ID, First Name and Last Name (Grade is optional).` };
  }
  const headers = best.headers;
  const idI = findHeader(headers, ID_HEADERS), fI = findHeader(headers, FIRST_HEADERS), lI = findHeader(headers, LAST_HEADERS);
  const gI = headers.findIndex((h) => h === 'grade' || h === 'grade level' || h === 'grade_level' || h.startsWith('grade'));
  const rows = [];
  for (let i = best.index + 1; i < lines.length; i++) {
    const v = lines[i].split(',').map((x) => x.trim());
    if (v.length > Math.max(idI, fI, lI)) {
      const studentId = v[idI] || '', firstName = v[fI] || '', lastName = v[lI] || '';
      const gradeRaw = gI >= 0 ? (v[gI] || '') : '';
      const gp = gradeRaw === '' ? null : parseInt(gradeRaw, 10);
      const grade = gp != null && !Number.isNaN(gp) ? gp : null;
      if (studentId || firstName || lastName) rows.push({ rowNumber: i + 1, studentId, firstName, lastName, grade });
    }
  }
  if (rows.length === 0) return { rows: [], error: 'We found the header row but no student rows under it. Add your students below the header and try again.' };
  return { rows, error: null };
}

function StudentsTable({ students, bags, users, invites, schoolId, onRefreshStudents, onRefreshUsers, onRefreshInvites }) {
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
  const [showArchived, setShowArchived] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [newStudent, setNewStudent] = useState({
    firstName: '',
    lastName: '',
    studentId: ''
  });
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [studentToDelete, setStudentToDelete] = useState(null);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteData, setInviteData] = useState({
    role: 'teacher',
    emails: ''
  });
  // Roster bulk upload
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [bulkFile, setBulkFile] = useState(null);
  const [bulkMode, setBulkMode] = useState('keep'); // 'keep' | 'archive'
  const [bulkLoading, setBulkLoading] = useState(false);
  const [bulkError, setBulkError] = useState(null);
  const [bulkResult, setBulkResult] = useState(null);

  const handleDownloadTemplate = () => {
    const blob = new Blob([STUDENT_TEMPLATE_CSV], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'student-import-template.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleBulkUpload = async () => {
    if (!bulkFile || bulkLoading) return;
    setBulkLoading(true);
    setBulkError(null);
    setBulkResult(null);
    try {
      const text = await bulkFile.text();
      const { rows, error } = parseRosterCsv(text);
      if (error) { setBulkError(error); setBulkLoading(false); return; }
      const apiEndpoint = process.env.REACT_APP_API_ENDPOINT;
      const username = localStorage.getItem('admin_username');
      const password = localStorage.getItem('admin_password');
      const response = await fetch(`${apiEndpoint}/admin/schools/${schoolId}/import-students`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': 'Basic ' + btoa(`${username}:${password}`) },
        body: JSON.stringify({
          schoolId,
          archiveMissingStudents: bulkMode === 'archive',
          updateExistingStudents: true,
          rows,
        }),
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        setBulkError(err.error || 'Import failed. Please try again.');
        setBulkLoading(false);
        return;
      }
      const data = await response.json();
      setBulkResult(data);
      if (onRefreshStudents) await onRefreshStudents();
    } catch (e) {
      console.error('Bulk import error:', e);
      setBulkError('Could not read or import that file. Please upload a .csv and try again.');
    } finally {
      setBulkLoading(false);
    }
  };

  const closeBulkModal = () => {
    if (bulkLoading) return;
    setShowBulkModal(false);
    setBulkFile(null);
    setBulkMode('keep');
    setBulkError(null);
    setBulkResult(null);
  };

  const [showDuplicateWarning, setShowDuplicateWarning] = useState(false);
  const [duplicateStudent, setDuplicateStudent] = useState(null);

  // Debounce search query
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Create a map of bag ID to bag for quick lookup
  const bagMap = useMemo(() => {
    const map = {};
    bags.forEach(bag => {
      map[bag.id] = bag;
    });
    return map;
  }, [bags]);

  // Enrich students with bag data for better performance
  const enrichedStudents = useMemo(() => {
    return students.map(student => {
      const bag = student.faradayBagId ? bagMap[student.faradayBagId] : null;
      return {
        ...student,
        bagSerialNumber: bag ? bag.serialNumber : '-',
        bagDamaged: bag ? bag.damaged : null,
        bagLost: bag ? bag.lost : null,
        bagDamagedSort: bag ? (bag.damaged ? 1 : 0) : -1,
        bagLostSort: bag ? (bag.lost ? 1 : 0) : -1,
      };
    });
  }, [students, bagMap]);

  // Define table columns
  const columns = useMemo(
    () => [
      {
        Header: 'Student ID',
        accessor: 'studentId',
      },
      {
        Header: 'First Name',
        accessor: 'firstName',
      },
      {
        Header: 'Last Name',
        accessor: 'lastName',
      },
      {
        Header: 'Serial #',
        accessor: 'bagSerialNumber',
      },
      {
        Header: 'Damaged',
        accessor: 'bagDamaged',
        sortType: (rowA, rowB) => {
          return rowA.original.bagDamagedSort - rowB.original.bagDamagedSort;
        },
        Cell: ({ value }) => {
          if (value === null) return <span>-</span>;
          if (value === true) return <span className="status-damaged">Yes</span>;
          return <span>-</span>;
        },
      },
      {
        Header: 'Lost',
        accessor: 'bagLost',
        sortType: (rowA, rowB) => {
          return rowA.original.bagLostSort - rowB.original.bagLostSort;
        },
        Cell: ({ value }) => {
          if (value === null) return <span>-</span>;
          if (value === true) return <span className="status-lost">Yes</span>;
          return <span>-</span>;
        },
      },
      {
        Header: '',
        id: 'actions',
        accessor: () => null,
        disableSortBy: true,
        Cell: ({ row }) => (
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleDeleteClick(row.original);
            }}
            className="delete-button"
            title="Delete student"
          >
            🗑️
          </button>
        ),
      },
    ],
    []
  );

  // Filter students based on search query and archived status
  const filteredStudents = useMemo(() => {
    let filtered = enrichedStudents;
    
    // Filter out archived students if checkbox is not checked
    if (!showArchived) {
      filtered = filtered.filter(student => !student.archived);
    }
    
    // Apply search query (using debounced value)
    if (debouncedSearchQuery) {
      const query = debouncedSearchQuery.toLowerCase();
      filtered = filtered.filter(student => {
        const studentId = student.studentId ? student.studentId.toLowerCase() : '';
        const firstName = student.firstName ? student.firstName.toLowerCase() : '';
        const lastName = student.lastName ? student.lastName.toLowerCase() : '';
        const bagSerial = student.bagSerialNumber !== '-' ? student.bagSerialNumber.toLowerCase() : '';
        
        return studentId.includes(query) || firstName.includes(query) || lastName.includes(query) || bagSerial.includes(query);
      });
    }
    
    return filtered;
  }, [enrichedStudents, debouncedSearchQuery, showArchived]);

  // Use the useTable hook with sorting and pagination
  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    page,
    prepareRow,
    canPreviousPage,
    canNextPage,
    pageOptions,
    pageCount,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,
    state: { pageIndex, pageSize },
  } = useTable(
    {
      columns,
      data: filteredStudents,
      initialState: { pageIndex: 0, pageSize: 50 },
    },
    useSortBy,
    usePagination
  );

  // Reset to first page when filters change
  useEffect(() => {
    gotoPage(0);
  }, [showArchived, debouncedSearchQuery, gotoPage]);

  // Check for duplicate student
  const checkForDuplicate = () => {
    const duplicate = students.find(student => {
      const sameFirstName = student.firstName?.toLowerCase() === newStudent.firstName?.toLowerCase();
      const sameLastName = student.lastName?.toLowerCase() === newStudent.lastName?.toLowerCase();
      const sameStudentId = newStudent.studentId && student.studentId?.toLowerCase() === newStudent.studentId?.toLowerCase();
      
      // Match if same first AND last name, OR same student ID (if provided)
      return (sameFirstName && sameLastName) || sameStudentId;
    });
    
    return duplicate;
  };

  const handleAddStudentClick = () => {
    const duplicate = checkForDuplicate();
    if (duplicate) {
      setDuplicateStudent(duplicate);
      setShowDuplicateWarning(true);
    } else {
      handleAddStudent();
    }
  };

  const handleAddStudent = async () => {
    // Close duplicate warning if open
    setShowDuplicateWarning(false);
    setDuplicateStudent(null);

    try {
      const apiEndpoint = process.env.REACT_APP_API_ENDPOINT;
      const username = localStorage.getItem('admin_username');
      const password = localStorage.getItem('admin_password');

      const requestBody = {
        schoolId: schoolId,
        students: [
          {
            firstName: newStudent.firstName,
            lastName: newStudent.lastName,
            studentId: newStudent.studentId || ""
          }
        ]
      };

      const response = await fetch(`${apiEndpoint}/admin/add-students`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': 'Basic ' + btoa(`${username}:${password}`)
        },
        body: JSON.stringify(requestBody)
      });

      console.log('Add student response:', response);

      if (response.ok) {
        console.log('Student added successfully');
        alert('Student added successfully');
        
      // Close modal and reset form
      setShowAddModal(false);
      setNewStudent({ firstName: '', lastName: '', studentId: '' });

      // Refresh the students data
      if (onRefreshStudents) {
        await onRefreshStudents();
      }
      } else {
        console.error('Failed to add student:', response.status);
        alert('Failed to add student. Please try again.');
        
        // Close modal and reset form
        setShowAddModal(false);
        setNewStudent({ firstName: '', lastName: '', studentId: '' });
      }
    } catch (error) {
      console.error('Error adding student:', error);
      alert('Error adding student. Please try again.');
      
      // Close modal and reset form
      setShowAddModal(false);
      setNewStudent({ firstName: '', lastName: '', studentId: '' });
    }
  };

  const handleCloseModal = () => {
    setShowAddModal(false);
    setNewStudent({ firstName: '', lastName: '', studentId: '' });
    setShowDuplicateWarning(false);
    setDuplicateStudent(null);
  };

  const handleDuplicateCancel = () => {
    setShowDuplicateWarning(false);
    setDuplicateStudent(null);
  };

  const handleDeleteClick = (student) => {
    setStudentToDelete(student);
    setShowDeleteConfirm(true);
  };

  const handleDeleteConfirm = async () => {
    if (!studentToDelete) return;

    try {
      const apiEndpoint = process.env.REACT_APP_API_ENDPOINT;
      const username = localStorage.getItem('admin_username');
      const password = localStorage.getItem('admin_password');

      const response = await fetch(`${apiEndpoint}/admin/students/${studentToDelete.id}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': 'Basic ' + btoa(`${username}:${password}`)
        }
      });

      console.log('Delete student response:', response);

      if (response.ok) {
        console.log('Student deleted successfully');
        alert('Student deleted successfully');
        
        // Close confirmation dialog
        setShowDeleteConfirm(false);
        setStudentToDelete(null);
        
        // Refresh the students data
        if (onRefreshStudents) {
          await onRefreshStudents();
        }
      } else {
        console.error('Failed to delete student:', response.status);
        alert('Failed to delete student. Please try again.');
        setShowDeleteConfirm(false);
        setStudentToDelete(null);
      }
    } catch (error) {
      console.error('Error deleting student:', error);
      alert('Error deleting student. Please try again.');
      setShowDeleteConfirm(false);
      setStudentToDelete(null);
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteConfirm(false);
    setStudentToDelete(null);
  };

  const handleInviteUser = async () => {
    try {
      const apiEndpoint = process.env.REACT_APP_API_ENDPOINT;
      const username = localStorage.getItem('admin_username');
      const password = localStorage.getItem('admin_password');

      // Parse comma-separated emails
      const emailList = inviteData.emails
        .split(',')
        .map(email => email.trim())
        .filter(email => email.length > 0);

      if (emailList.length === 0) {
        alert('Please enter at least one email address');
        return;
      }

      const requestBody = {
        emails: emailList,
        role: inviteData.role,
        schoolId: schoolId
      };

      const response = await fetch(`${apiEndpoint}/invite`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': 'Basic ' + btoa(`${username}:${password}`)
        },
        body: JSON.stringify(requestBody)
      });

      console.log('Invite user response:', response);

      if (response.ok) {
        console.log('User(s) invited successfully');
        alert('User(s) invited successfully');

        // Close modal and reset form
        setShowInviteModal(false);
        setInviteData({ role: 'teacher', emails: '' });

        // Refresh the invites data
        if (onRefreshInvites) {
          await onRefreshInvites();
        }
      } else {
        console.error('Failed to invite user(s):', response.status);
        alert('Failed to invite user(s). Please try again.');

        // Close modal and reset form
        setShowInviteModal(false);
        setInviteData({ role: 'teacher', emails: '' });
      }
    } catch (error) {
      console.error('Error inviting user(s):', error);
      alert('Error inviting user(s). Please try again.');

      // Close modal and reset form
      setShowInviteModal(false);
      setInviteData({ role: 'teacher', emails: '' });
    }
  };

  const handleCloseInviteModal = () => {
    setShowInviteModal(false);
    setInviteData({ role: 'teacher', emails: '' });
  };

  return (
    <>
      <div className="students-header">
        <div className="students-left">
          <h3>Students ({filteredStudents.length})</h3>
          <label className="checkbox-label">
            <input
              type="checkbox"
              checked={showArchived}
              onChange={(e) => setShowArchived(e.target.checked)}
            />
            <span>Show Archived</span>
          </label>
          <div className="search-container">
            <input
              type="text"
              className="search-input"
              placeholder="Search by name, student ID, or bag serial..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            {searchQuery && (
              <button 
                className="clear-search"
                onClick={() => setSearchQuery('')}
                aria-label="Clear search"
              >
                ×
              </button>
            )}
          </div>
        </div>
        <div className="students-right">
          <button onClick={() => setShowAddModal(true)} className="add-student-button">
            + Add Student
          </button>
          <button onClick={() => setShowBulkModal(true)} className="add-student-button">
            ⬆ Bulk Upload
          </button>
          <button onClick={() => setShowInviteModal(true)} className="invite-user-button">
            + Invite User
          </button>
        </div>
      </div>
      
      <div className="students-and-users-container">
        <div className="students-section">
          {students.length === 0 ? (
            <div className="no-data">No students found for this school</div>
          ) : filteredStudents.length === 0 ? (
            <div className="no-data">No students match your search</div>
          ) : (
        <div className="table-container">
          <table {...getTableProps()} className="students-table">
            <thead>
              {headerGroups.map((headerGroup, i) => (
                <tr {...headerGroup.getHeaderGroupProps()} key={i}>
                  {headerGroup.headers.map((column, j) => (
                    <th {...column.getHeaderProps(column.getSortByToggleProps())} key={j}>
                      <div className="th-content">
                        {column.render('Header')}
                        <span className="sort-indicator">
                          {column.isSorted
                            ? column.isSortedDesc
                              ? ' ↓'
                              : ' ↑'
                            : ''}
                        </span>
                      </div>
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody {...getTableBodyProps()}>
              {page.map((row, i) => {
                prepareRow(row);
                return (
                  <tr {...row.getRowProps()} key={i}>
                    {row.cells.map((cell, j) => (
                      <td {...cell.getCellProps()} key={j}>
                        {cell.render('Cell')}
                      </td>
                    ))}
                  </tr>
                );
              })}
            </tbody>
          </table>
          <div className="pagination">
            <button onClick={() => gotoPage(0)} disabled={!canPreviousPage}>
              {'<<'}
            </button>
            <button onClick={() => previousPage()} disabled={!canPreviousPage}>
              {'<'}
            </button>
            <button onClick={() => nextPage()} disabled={!canNextPage}>
              {'>'}
            </button>
            <button onClick={() => gotoPage(pageCount - 1)} disabled={!canNextPage}>
              {'>>'}
            </button>
            <span>
              Page{' '}
              <strong>
                {pageIndex + 1} of {pageOptions.length}
              </strong>{' '}
            </span>
            <span>
              | Go to page:{' '}
              <input
                type="number"
                defaultValue={pageIndex + 1}
                onChange={e => {
                  const page = e.target.value ? Number(e.target.value) - 1 : 0
                  gotoPage(page)
                }}
                style={{ width: '100px' }}
              />
            </span>
            <select
              value={pageSize}
              onChange={e => {
                setPageSize(Number(e.target.value))
              }}
            >
              {[10, 25, 50, 100, 200].map(pageSize => (
                <option key={pageSize} value={pageSize}>
                  Show {pageSize}
                </option>
              ))}
            </select>
          </div>
          </div>
        )}
      </div>

      <div className="right-panels">
        <SchoolUsers users={users} schoolId={schoolId} onRefreshUsers={onRefreshUsers} />
        <SchoolInvites invites={invites} onRefresh={onRefreshInvites} />
      </div>
    </div>

      {showAddModal && (
        <div className="modal-overlay" onClick={handleCloseModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Add New Student</h3>
              <button className="modal-close" onClick={handleCloseModal}>×</button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label htmlFor="firstName">First Name</label>
                <input
                  type="text"
                  id="firstName"
                  value={newStudent.firstName}
                  onChange={(e) => setNewStudent({ ...newStudent, firstName: e.target.value })}
                  placeholder="Enter first name"
                />
              </div>
              <div className="form-group">
                <label htmlFor="lastName">Last Name</label>
                <input
                  type="text"
                  id="lastName"
                  value={newStudent.lastName}
                  onChange={(e) => setNewStudent({ ...newStudent, lastName: e.target.value })}
                  placeholder="Enter last name"
                />
              </div>
              <div className="form-group">
                <label htmlFor="studentId">Student ID (Optional)</label>
                <input
                  type="text"
                  id="studentId"
                  value={newStudent.studentId}
                  onChange={(e) => setNewStudent({ ...newStudent, studentId: e.target.value })}
                  placeholder="Enter student ID"
                />
              </div>
            </div>
            <div className="modal-footer">
              <button className="modal-button cancel" onClick={handleCloseModal}>
                Cancel
              </button>
              <button 
                className="modal-button submit" 
                onClick={handleAddStudentClick}
                disabled={!newStudent.firstName || !newStudent.lastName}
              >
                Add Student
              </button>
            </div>
          </div>
        </div>
      )}

      {showDuplicateWarning && duplicateStudent && (
        <div className="modal-overlay" onClick={handleDuplicateCancel}>
          <div className="modal-content confirm-dialog" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Duplicate Student Warning</h3>
              <button className="modal-close" onClick={handleDuplicateCancel}>×</button>
            </div>
            <div className="modal-body">
              <p>A student with similar information already exists:</p>
              <p className="student-details">
                <strong>{duplicateStudent.firstName} {duplicateStudent.lastName}</strong>
                <br />
                Student ID: {duplicateStudent.studentId || 'N/A'}
              </p>
              <p className="warning-text">Do you want to add this student anyway?</p>
            </div>
            <div className="modal-footer">
              <button className="modal-button cancel" onClick={handleDuplicateCancel}>
                Cancel
              </button>
              <button className="modal-button submit" onClick={handleAddStudent}>
                Add Anyway
              </button>
            </div>
          </div>
        </div>
      )}

      {showDeleteConfirm && studentToDelete && (
        <div className="modal-overlay" onClick={handleDeleteCancel}>
          <div className="modal-content confirm-dialog" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Confirm Deletion</h3>
              <button className="modal-close" onClick={handleDeleteCancel}>×</button>
            </div>
            <div className="modal-body">
              <p>Are you sure you want to delete this student?</p>
              <p className="student-details">
                <strong>{studentToDelete.firstName} {studentToDelete.lastName}</strong>
                <br />
                Student ID: {studentToDelete.studentId}
              </p>
              <p className="warning-text">This action cannot be undone.</p>
            </div>
            <div className="modal-footer">
              <button className="modal-button cancel" onClick={handleDeleteCancel}>
                Cancel
              </button>
              <button className="modal-button delete-confirm" onClick={handleDeleteConfirm}>
                Delete Student
              </button>
            </div>
          </div>
        </div>
      )}

      {showBulkModal && (
        <div className="modal-overlay" onClick={closeBulkModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Bulk upload roster</h3>
              <button className="modal-close" onClick={closeBulkModal}>×</button>
            </div>
            <div className="modal-body">
              {bulkResult ? (
                <div className="form-group">
                  <p>Import complete for this school:</p>
                  <ul>
                    <li><strong>{bulkResult.numberImported}</strong> added</li>
                    <li><strong>{bulkResult.numberUpdated}</strong> updated</li>
                    <li><strong>{bulkResult.numberArchived}</strong> archived</li>
                  </ul>
                </div>
              ) : (
                <>
                  <div className="form-group">
                    <button className="add-student-button" onClick={handleDownloadTemplate}>Download template</button>
                  </div>
                  <div className="form-group">
                    <label>How should this affect students already in the app?</label>
                    <label style={{ display: 'flex', gap: 8, alignItems: 'flex-start', marginTop: 6 }}>
                      <input type="radio" name="bulkMode" checked={bulkMode === 'keep'} onChange={() => setBulkMode('keep')} />
                      <span><strong>Keep everyone</strong><br />Match by Student ID or name, add new students, update existing. No one is archived.</span>
                    </label>
                    <label style={{ display: 'flex', gap: 8, alignItems: 'flex-start', marginTop: 8 }}>
                      <input type="radio" name="bulkMode" checked={bulkMode === 'archive'} onChange={() => setBulkMode('archive')} />
                      <span><strong>Replace roster</strong><br />Same matching, then archives anyone already in the app who isn't in the file (their history is kept).</span>
                    </label>
                  </div>
                  <div className="form-group">
                    <label htmlFor="bulkFile">Roster file (.csv)</label>
                    <input id="bulkFile" type="file" accept=".csv,text/csv" onChange={(e) => { setBulkFile(e.target.files?.[0] || null); setBulkError(null); }} />
                    {bulkFile && <div style={{ marginTop: 4, fontSize: 13 }}>{bulkFile.name}</div>}
                  </div>
                  {bulkError && <div className="form-group" style={{ color: '#C0362C' }}>{bulkError}</div>}
                </>
              )}
            </div>
            <div className="modal-footer">
              <button className="modal-button cancel" onClick={closeBulkModal}>{bulkResult ? 'Close' : 'Cancel'}</button>
              {!bulkResult && (
                <button className="modal-button submit" onClick={handleBulkUpload} disabled={!bulkFile || bulkLoading}>
                  {bulkLoading ? 'Importing…' : 'Start Importing'}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {showInviteModal && (
        <div className="modal-overlay" onClick={handleCloseInviteModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Invite User</h3>
              <button className="modal-close" onClick={handleCloseInviteModal}>×</button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label htmlFor="role">Role</label>
                <select
                  id="role"
                  value={inviteData.role}
                  onChange={(e) => setInviteData({ ...inviteData, role: e.target.value })}
                  className="role-select"
                >
                  <option value="teacher">Teacher</option>
                  <option value="administrator">Administrator</option>
                </select>
              </div>
              <div className="form-group">
                <label htmlFor="emails">Email Addresses</label>
                <textarea
                  id="emails"
                  value={inviteData.emails}
                  onChange={(e) => setInviteData({ ...inviteData, emails: e.target.value })}
                  placeholder="Enter email addresses separated by commas (e.g., john@example.com, jane@example.com)"
                  rows="4"
                  className="email-textarea"
                />
                <small className="form-hint">Separate multiple email addresses with commas</small>
              </div>
            </div>
            <div className="modal-footer">
              <button className="modal-button cancel" onClick={handleCloseInviteModal}>
                Cancel
              </button>
              <button
                className="modal-button submit"
                onClick={handleInviteUser}
                disabled={!inviteData.emails.trim()}
              >
                Send Invites
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default StudentsTable;

